import { FC, useState } from 'react'
import { useStore } from '@tanstack/react-store'

import { Panels, LoadingContainer, Modal } from 'peach/components'
import { withTrigger } from 'peach/decorators'
import { getName } from 'peach/helpers'

import { authStore } from 'core/auth/authStore'
import { usePreview } from 'core/types'

import TemplateCodeEditor from './TemplateCodeEditor'
import TemplateRenderOptions from './TemplateRenderOptions'
import TemplateRenderPreview from './TemplateRenderPreview'

type TemplateEditorProps = {
  template: any
  descriptor: any
  onSaveTemplate: (val: any) => void
}

const TemplateEditor: FC<TemplateEditorProps> = ({
  template,
  descriptor,
  onSaveTemplate,
  ...rest
}) => {
  const currentAuthStoreState = useStore(authStore)

  const { subject, channel } = descriptor || {}

  const [state, setState] = useState(() => ({
    template,
    renderOptions: {
      fmt: 'html',
      personId: currentAuthStoreState.decodedToken?.personId,
    },
  }))

  const {
    mutateAsync: renderPreview,
    isPending,
    data: preview,
    error: renderError,
  } = usePreview({})

  const onRender = async () => {
    const { renderOptions } = state
    const { fmt, personId, ...rest } = renderOptions

    if (!personId) {
      throw new Error('Person ID is required to render a preview.')
    }

    return await renderPreview({
      subject,
      channel,
      personId,
      ...rest,
    })
  }

  return (
    <Modal
      fullContent
      hideTrigger
      initialValue={state}
      onChange={setState}
      onSubmit={onSaveTemplate}
      submitLabel='Save'
      title={getName(descriptor)}
      value={state}
      width='90%'
      {...rest}
    >
      <Panels
        left={<TemplateCodeEditor formKey='template' />}
        leftWidth='40%'
        right={<TemplateRenderOptions formKey='renderOptions' />}
      >
        <LoadingContainer error={renderError} isLoading={isPending}>
          <TemplateRenderPreview onRender={onRender} preview={preview} />
        </LoadingContainer>
      </Panels>
    </Modal>
  )
}

export default withTrigger(TemplateEditor)
